import { useEffect, useState } from 'react';
import emailjs from '@emailjs/browser';
import '../styles/Contact.css';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';

const steps = [
	{
		id: 'consultation',
		title: 'Boka konsultation',
		description: 'Berätta om ditt behov så bokar vi ett möte.',
		fields: ['role', 'area', 'name', 'company', 'orgnr', 'address', 'email', 'phone', 'dates', 'message'],
	},
	{
		id: 'contact',
		title: 'Kontakta oss',
		description: 'Snabba frågor? Hör av dig så svarar vi.',
		fields: ['name', 'email', 'phone', 'dates', 'message'],
	},
];

const initialFormState = {
	role: 'privat',
	area: '',
	scope: '',
	frequency: '',
	name: '',
	company: '',
	orgnr: '',
	address: '',
	email: '',
	phone: '',
	dates: [],
	message: '',
};

function Contact() {
	const [activeTab, setActiveTab] = useState('consultation');
	const [formData, setFormData] = useState(initialFormState);
	const [dateInput, setDateInput] = useState('');
	const [errors, setErrors] = useState({});
	const [isSending, setIsSending] = useState(false);
	const [status, setStatus] = useState({ type: '', message: '' });
	const hasEmailJsConfig = Boolean(SERVICE_ID && TEMPLATE_ID && PUBLIC_KEY);

	const scrollToField = (fieldId) => {
		const el = document.getElementById(fieldId);
		if (!el) return;
		el.scrollIntoView({ behavior: 'smooth', block: 'center' });
		el.classList.add('field-pulse');
		if (typeof el.focus === 'function') {
			el.focus({ preventScroll: true });
		}
		window.setTimeout(() => el.classList.remove('field-pulse'), 1200);
	};

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const messagePrefill = params.get('message');
		if (messagePrefill) {
			setFormData((prev) => (
				prev.message
					? prev
					: { ...prev, message: messagePrefill }
			));
		}
	}, []);

	useEffect(() => {
		const exists = steps.find((s) => s.id === activeTab);
		if (!exists && steps.length > 0) {
			setActiveTab(steps[0].id);
		}
	}, [activeTab]);

	const handleChange = (event) => {
		const { name, value } = event.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const addDate = () => {
		if (!dateInput) return;
		setFormData((prev) => {
			if (prev.dates.includes(dateInput)) return prev;
			return { ...prev, dates: [...prev.dates, dateInput].sort() };
		});
		setDateInput('');
	};

	const removeDate = (date) => {
		setFormData((prev) => ({ ...prev, dates: prev.dates.filter((d) => d !== date) }));
	};

	const validate = (fields) => {
		const nextErrors = {};
		const emailRegex = /[^\s@]+@[^\s@]+\.[^\s@]+/;
		const phoneRegex = /^[0-9+\-()\s]{6,}$/;

		if (fields.includes('name') && !formData.name.trim()) {
			nextErrors.name = 'Namn krävs';
		}
		if (fields.includes('email') && !emailRegex.test(formData.email.trim())) {
			nextErrors.email = 'Ogiltig e-post';
		}
		if (fields.includes('phone') && !phoneRegex.test(formData.phone.trim())) {
			nextErrors.phone = 'Ogiltigt telefonnummer';
		}
		if (fields.includes('area') && !formData.area.trim()) {
			nextErrors.area = 'Ange yta i kvm';
		}
		if (fields.includes('address') && !formData.address.trim()) {
			nextErrors.address = 'Adress krävs';
		}
		if (fields.includes('scope') && !formData.scope.trim()) {
			nextErrors.scope = 'Vad ska städas?';
		}
		if (fields.includes('frequency') && !formData.frequency.trim()) {
			nextErrors.frequency = 'Välj frekvens';
		}
		if (fields.includes('orgnr') && formData.role === 'foretag' && !formData.orgnr.trim()) {
			nextErrors.orgnr = 'Org.nr krävs för företag';
		}
		if (fields.includes('company') && formData.role === 'foretag' && !formData.company.trim()) {
			nextErrors.company = 'Företagsnamn krävs';
		}
		return nextErrors;
	};

	const buildTemplateParams = (tab) => ({
		role: formData.role,
		area: formData.area,
		scope: formData.scope,
		frequency: formData.frequency,
		name: formData.name,
		company: formData.company || 'N/A',
		orgnr: formData.orgnr || 'N/A',
		address: formData.address,
		email: formData.email,
		phone: formData.phone,
		dates: formData.dates.join(', '),
		message: formData.message,
		time: new Date().toLocaleString('sv-SE'),
		form_type: tab?.id || 'unknown',
	});

	const handleSubmit = async (event) => {
		event.preventDefault();
		setStatus({ type: '', message: '' });
		const tab = steps.find((s) => s.id === activeTab) || steps[0];
		const nextErrors = validate(tab.fields);
		setErrors(nextErrors);
		if (Object.keys(nextErrors).length > 0) {
			const firstErrorField = tab.fields.find((field) => nextErrors[field]);
			if (firstErrorField) {
				scrollToField(firstErrorField);
			}
			return;
		}
		if (!hasEmailJsConfig) {
			setStatus({ type: 'error', message: 'Saknade EmailJS-nycklar. Lägg till dem i .env.local och starta om.' });
			return;
		}

		setIsSending(true);
		try {
			await emailjs.send(
				SERVICE_ID,
				TEMPLATE_ID,
				buildTemplateParams(tab),
				{ publicKey: PUBLIC_KEY },
			);
			setStatus({ type: 'success', message: 'Tack! Vi återkommer snart.' });
			setFormData(initialFormState);
			setDateInput('');
		} catch (error) {
			console.error('EmailJS error', error);
			setStatus({ type: 'error', message: 'Kunde inte skicka just nu. Försök igen om en stund eller mejla oss direkt.' });
		} finally {
			setIsSending(false);
		}
	};

	const renderField = (field) => {
		switch (field) {
			case 'role':
				return (
					<div className="form-group inline" key="role">
						<label>Du är</label>
						<div className="chip-row">
							<button
								type="button"
								className={`chip ${formData.role === 'privat' ? 'active' : ''}`}
								onClick={() => setFormData((prev) => ({ ...prev, role: 'privat' }))}
								aria-pressed={formData.role === 'privat'}
							>
								Privatperson
							</button>
							<button
								type="button"
								className={`chip ${formData.role === 'foretag' ? 'active' : ''}`}
								onClick={() => setFormData((prev) => ({ ...prev, role: 'foretag' }))}
								aria-pressed={formData.role === 'foretag'}
							>
								Företag
							</button>
						</div>
					</div>
				);
			case 'area':
				return (
					<div className="form-group" key="area">
						<label htmlFor="area">Yta (kvm)</label>
						<input
							id="area"
							name="area"
							type="number"
							min="0"
							placeholder="t.ex. 120"
							value={formData.area}
							onChange={handleChange}
							required
						/>
						{errors.area && <span className="error">{errors.area}</span>}
					</div>
				);
			case 'name':
				return (
					<div className="form-group" key="name">
						<label htmlFor="name">Namn och efternamn</label>
						<input
							id="name"
							name="name"
							type="text"
							placeholder="Ditt namn"
							value={formData.name}
							onChange={handleChange}
							required
						/>
						{errors.name && <span className="error">{errors.name}</span>}
					</div>
				);
			case 'company':
				if (formData.role !== 'foretag') return null;
				return (
					<div className="form-group" key="company">
						<label htmlFor="company">Företagsnamn</label>
						<input
							id="company"
							name="company"
							type="text"
							placeholder="Företagsnamn"
							value={formData.company}
							onChange={handleChange}
							disabled={formData.role !== 'foretag'}
						/>
						{errors.company && <span className="error">{errors.company}</span>}
					</div>
				);
			case 'orgnr':
				if (formData.role !== 'foretag') return null;
				return (
					<div className="form-group" key="orgnr">
						<label htmlFor="orgnr">Org.nr</label>
						<input
							id="orgnr"
							name="orgnr"
							type="text"
							placeholder="ÅÅÅÅMMDD-XXXX eller organisationsnummer"
							value={formData.orgnr}
							onChange={handleChange}
							disabled={formData.role !== 'foretag'}
						/>
						{errors.orgnr && <span className="error">{errors.orgnr}</span>}
					</div>
				);
			case 'address':
				return (
					<div className="form-group" key="address">
						<label htmlFor="address">Adress</label>
						<input
							id="address"
							name="address"
							type="text"
							placeholder="Gatuadress, ort"
							value={formData.address}
							onChange={handleChange}
							required
						/>
						{errors.address && <span className="error">{errors.address}</span>}
					</div>
				);
			case 'email':
				return (
					<div className="form-group" key="email">
						<label htmlFor="email">E-post</label>
									<input
							id="email"
							name="email"
							type="email"
							placeholder="namn@exempel.se"
							value={formData.email}
							onChange={handleChange}
							required
						/>
						{errors.email && <span className="error">{errors.email}</span>}
										
											<div className="form-group" key="scope">
												<label htmlFor="scope">Vad ska städas?</label>
												<select
													id="scope"
													name="scope"
													value={formData.scope}
													onChange={handleChange}
													required
												>
													<option value="" disabled>Välj objekt</option>
													<option value="Lägenhet">Lägenhet</option>
													<option value="Villa/Radhus">Villa/Radhus</option>
													<option value="Kontor">Kontor</option>
													<option value="Butik">Butik</option>
													<option value="Bygg/entreprenad">Bygg/entreprenad</option>
													<option value="Annat">Annat</option>
												</select>
												{errors.scope && <span className="error">{errors.scope}</span>}
											</div>
										
									
											<div className="form-group" key="frequency">
												<label htmlFor="frequency">Frekvens</label>
												<select
													id="frequency"
													name="frequency"
													value={formData.frequency}
													onChange={handleChange}
													required
												>
													<option value="" disabled>Välj frekvens</option>
													<option value="Engångsuppdrag">Engångsuppdrag</option>
													<option value="Varje vecka">Varje vecka</option>
													<option value="Varannan vecka">Varannan vecka</option>
													<option value="Varje månad">Varje månad</option>
													<option value="Efter behov">Efter behov</option>
												</select>
												{errors.frequency && <span className="error">{errors.frequency}</span>}
											</div>
										

						
					</div>
				);
			case 'phone':
				return (
					<div className="form-group" key="phone">
						<label htmlFor="phone">Telefon</label>
						<input
							id="phone"
							name="phone"
							type="tel"
							placeholder="08-123 45 67"
							value={formData.phone}
							onChange={handleChange}
							required
						/>
						{errors.phone && <span className="error">{errors.phone}</span>}
					</div>
				);
			case 'dates':
				return (
					<div className="form-group" key="dates">
						<div className="label-row">
							<label htmlFor="dates">Önskade datum (valfritt)</label>
							<span className="optional-pill">Valfritt</span>
						</div>
						<div className="date-row">
							<input
								id="dates"
								type="date"
								value={dateInput}
								onChange={(event) => setDateInput(event.target.value)}
								min={new Date().toISOString().split('T')[0]}
								aria-label="Välj datum"
							/>
							<button type="button" className="date-add" onClick={addDate} disabled={!dateInput}>
								Lägg till
							</button>
						</div>
						{formData.dates.length > 0 && (
							<div className="date-chips" aria-label="Valda datum">
								{formData.dates.map((date) => (
									<span className="date-chip" key={date}>
										{date}
										<button type="button" aria-label={`Ta bort ${date}`} onClick={() => removeDate(date)}>
											×
										</button>
									</span>
								))}
							</div>
						)}
						<p className="field-hint">Välj ett eller flera datum - vi bekräftar vad som passar.</p>
					</div>
				);
			case 'message':
				return (
					<div className="form-group message-group" key="message">
						<label htmlFor="message">Meddelande</label>
						<textarea
							id="message"
							name="message"
							rows="4"
							placeholder="Berätta kort vad du behöver hjälp med"
							value={formData.message}
							onChange={handleChange}
						/>
					</div>
				);
			default:
				return null;
		}
	};

	const activeStep = steps.find((s) => s.id === activeTab) || steps[0];

	return (
		<section className="contact-section" id="contact" aria-labelledby="contact-title">
			<div className="contact-shell">
				<div className="contact-header">
					<span className="contact-kicker">Kontakt</span>
					<h2 id="contact-title">Hur vill du komma i kontakt?</h2>
					<p>Välj det alternativ som passar dig - vi återkopplar snabbt med nästa steg.</p>
				</div>

				<div className="contact-tabs" role="tablist" aria-label="Kontaktval">
					{steps.map((step) => (
						<button
							key={step.id}
							role="tab"
							aria-selected={activeTab === step.id}
							className={`contact-tab ${activeTab === step.id ? 'active' : ''}`}
							onClick={() => setActiveTab(step.id)}
						>
							<span>{step.title}</span>
							<small>{step.description}</small>
						</button>
					))}
				</div>

				<form className="contact-form" onSubmit={handleSubmit} noValidate>
					<div className="form-grid">
						{activeStep.fields.map((field) => renderField(field))}
					</div>
					<button type="submit" className="contact-submit" disabled={isSending} aria-busy={isSending}>
						{isSending ? 'Skickar…' : 'Skicka'}
					</button>
					{status.message && (
						<p className={`form-status ${status.type}`} aria-live="polite">
							{status.message}
						</p>
					)}
				</form>
			</div>
		</section>
	);
}

export default Contact;
